import type { MaybeRefOrGetter } from 'vue';
import type { CollectionFieldPath, ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import { getMonthRangeForDate } from '#layers/base/public';
import { useFilterGate } from '#layers/filters/public';
import { buildCalendarRangeFilter, isCalendarDateField } from '#layers/listing/components/Layout/Calendar/build-calendar-range-filter';
import { useOutputGate } from '#layers/listing/composables/use-output-gate';

/**
 * The date fields the calendar may place events by: date or datetime fields the list read returns,
 * whose visible-range clause the read filter accepts. The end field shares that clause with the
 * start field, so it is checked together with it.
 */
export function useCalendarDateFieldGate(collection: MaybeRefOrGetter<ResolvedMonospaceCollection | undefined>) {
	const outputGate = useOutputGate(collection);
	const filterGate = useFilterGate();
	// The read filter accepts or rejects the clause by its fields and operators, not its dates.
	const probeRange = getMonthRangeForDate();

	function isReturnedDateField(field: ResolvedMonospacePrimitiveField) {
		return isCalendarDateField(field) && outputGate.allowsPath([field.id] as CollectionFieldPath);
	}

	function allowsRange(startField: ResolvedMonospacePrimitiveField, endField?: ResolvedMonospacePrimitiveField) {
		const clause = buildCalendarRangeFilter({ startField, endField, range: probeRange });
		return !!clause && filterGate.allowsClause(clause);
	}

	function allowsStartField(field: ResolvedMonospacePrimitiveField): boolean {
		return isReturnedDateField(field) && allowsRange(field);
	}

	function allowsEndField(field: ResolvedMonospacePrimitiveField, startField: ResolvedMonospacePrimitiveField | null | undefined): boolean {
		return isReturnedDateField(field) && (!startField || allowsRange(startField, field));
	}

	return { allowsStartField, allowsEndField };
}
